import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import {
  computeTdsLines,
  fromMinor,
  invoiceNetPayableFor,
  tdsBaseFor,
  totalTds,
  totalTdsBase,
  type Invoice,
  type TdsLineInput,
  type TdsProfile,
} from '@fpc/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { useMoney } from '@/hooks/useMoney';
import { useLayout } from '@/hooks/useMediaQuery';
import { ResponsiveRecords } from '@/components/ResponsiveRecords';
import { ExpenseLedgerOptions, useExpenseLedgers } from '@/components/ExpenseLedgerOptions';
import {
  TdsLinesEditor,
  draftsOf,
  parseLines,
  withVendorProfiles,
  type LineDraft,
} from '@/components/TdsLinesEditor';
import { useTdsSections } from '@/hooks/useTdsSections';
import { RcmCheckbox } from '@/components/RcmFields';
import { formatDate, rupeesToMinor } from '@/lib/format';
import {
  Card,
  EmptyState,
  ErrorState,
  Money,
  PageHeader,
  Spinner,
  StatusBadge,
  Table,
  Tabs,
} from '@/components/ui';

type View = 'ACCOUNTING' | 'TREASURY';

/**
 * The accounting workbench.
 *
 * Everything that has cleared business approval and is waiting on the
 * accounting team, alongside what has been escalated. Selecting a row opens
 * the verification panel beside it, so the queue stays visible while a
 * deduction is keyed.
 */
export function AccountingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { companyId, verticalId } = useAuth();
  const [view, setView] = useState<View>('ACCOUNTING');
  // Below desktop the queue and the verification form take turns on screen
  // instead of sitting side by side.
  const layout = useLayout();
  const compact = layout !== 'desktop';

  const { data, isLoading, error } = useQuery({
    queryKey: ['accounting-queue', companyId, verticalId, view],
    queryFn: () => api.invoices.list({ companyId, verticalId, view, pageSize: 50 }),
  });

  const rows = data?.items ?? [];
  const selectedId = id ?? (compact ? undefined : rows[0]?.id);
  const showQueue = !compact || !id;

  return (
    <>
      <PageHeader
        title="Accounting"
        subtitle="Verify approved invoices, apply TDS, and release them for payment"
      />

      {compact && id ? (
        <Link className="btn-secondary mb-4" to="/accounting">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to queue
        </Link>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-5">
        {showQueue ? (
          <Card className="lg:col-span-2">
            <Tabs
              tabs={[
                { key: 'ACCOUNTING', label: 'To verify' },
                { key: 'TREASURY', label: 'With Treasury' },
              ]}
              active={view}
              onChange={(key) => {
                setView(key);
                navigate('/accounting');
              }}
            />

            {isLoading ? (
              <Spinner />
            ) : error ? (
              <div className="p-4">
                <ErrorState error={error} />
              </div>
            ) : !rows.length ? (
              <EmptyState
                illustration="approved"
                title={view === 'ACCOUNTING' ? 'Nothing to verify' : 'Nothing with Treasury'}
                hint={
                  view === 'ACCOUNTING'
                    ? 'Invoices arrive here once the business approvers have signed off.'
                    : undefined
                }
              />
            ) : (
              <ResponsiveRecords
                layout={layout}
                openLabel={view === 'TREASURY' ? 'View' : 'Verify'}
                records={rows.map((invoice) => ({
                  key: invoice.id,
                  href: `/accounting/${invoice.id}`,
                  title: invoice.vendorName ?? 'Vendor not read yet',
                  subtitle: invoice.trackingId,
                  heading: <span className="font-mono">{invoice.trackingId}</span>,
                  badge: <Money minor={invoice.totalAmount ?? 0} />,
                  details: [
                    { label: 'Vendor', value: invoice.vendorName ?? '—', notOnCard: true },
                    { label: 'Invoice', value: invoice.invoiceNumber ?? '—' },
                    {
                      label: 'Gross',
                      value: <Money minor={invoice.totalAmount ?? 0} />,
                      notOnCard: true,
                    },
                    { label: 'Due', value: formatDate(invoice.dueDate) },
                  ],
                }))}
                table={
                  <Table>
                    <thead className="thead">
                      <tr>
                        <th className="th">Tracking ID</th>
                        <th className="th">Vendor</th>
                        <th className="th text-right">Gross</th>
                      </tr>
                    </thead>
                    <tbody className="tbody">
                      {rows.map((invoice) => (
                        <tr
                          key={invoice.id}
                          className={
                            invoice.id === selectedId ? 'bg-brand-50' : 'hover:bg-slate-50'
                          }
                        >
                          <td className="td">
                            <Link
                              className="font-mono text-xs font-medium text-brand-700"
                              to={`/accounting/${invoice.id}`}
                            >
                              {invoice.trackingId}
                            </Link>
                          </td>
                          <td className="td">{invoice.vendorName ?? '—'}</td>
                          <td className="td text-right">
                            <Money minor={invoice.totalAmount ?? 0} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                }
              />
            )}
          </Card>
        ) : null}

        <div className="lg:col-span-3">
          {selectedId ? (
            <VerificationPanel invoiceId={selectedId} readOnly={view === 'TREASURY'} />
          ) : null}
        </div>
      </div>
    </>
  );
}

/**
 * The verification form for one invoice.
 *
 * The three figures are always shown together — gross, TDS, net — because the
 * whole point of the stage is that the vendor bills one amount and the bank
 * pays another.
 */
function VerificationPanel({ invoiceId, readOnly }: { invoiceId: string; readOnly: boolean }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const money = useMoney();
  const { company } = money;
  const { can } = useAuth();

  const {
    data: invoice,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => api.invoices.get(invoiceId),
  });

  const [tdsApplicable, setTdsApplicable] = useState(false);
  // GST under reverse charge, as set at review: accounting confirms or changes it.
  const [rcm, setRcm] = useState(false);
  const [lines, setLines] = useState<LineDraft[]>([]);
  const [expenseLedgerId, setExpenseLedgerId] = useState('');
  const [partyLedgerId, setPartyLedgerId] = useState('');
  const [advanceLedgerId, setAdvanceLedgerId] = useState('');
  const [costCentre, setCostCentre] = useState('');
  const [notes, setNotes] = useState('');
  const [remarks, setRemarks] = useState('');
  const [priority, setPriority] = useState<'P1' | 'P2'>('P2');
  /** How much of the vendor's advance settles this invoice, in rupees as typed. */
  const [advance, setAdvance] = useState('');
  const [advanceTouched, setAdvanceTouched] = useState(false);

  // The vendor's advance balance, which accounting may draw on.
  const { data: advanceLedger } = useQuery({
    queryKey: ['vendor-advance', invoice?.vendorId],
    queryFn: () => api.settings.vendorAdvance(invoice!.vendorId!),
    enabled: !!invoice?.vendorId && can('vendor_advance:read'),
  });

  const canTag = can('ledger:read');
  // The vendor's TDS profiles, so a line can be filled from one, and its
  // default expense ledger.
  const { data: vendor } = useQuery({
    queryKey: ['vendor', invoice?.vendorId],
    queryFn: () => api.settings.vendor(invoice!.vendorId!),
    enabled: !!invoice?.vendorId && (company.tds || canTag),
  });
  const profiles: TdsProfile[] = vendor?.tdsProfiles ?? [];
  // Each line's section and rate come from Settings → TDS Sections.
  const { sections: tdsSections, ratesFor, successorOf } = useTdsSections();

  // The company's expense and asset ledgers — what was bought — and the
  // vendor's own accounts, which the invoice is owed on.
  const { ledgers: expenseLedgers, loaded: expenseLedgersLoaded } = useExpenseLedgers(
    invoice?.companyId,
    canTag,
  );
  const { data: vendorLedgers } = useQuery({
    queryKey: ['vendor-ledgers', invoice?.vendorId],
    queryFn: () =>
      api.settings.ledgers({
        companyId: invoice!.companyId,
        vendorId: invoice!.vendorId!,
        pageSize: 100,
      }),
    enabled: !!invoice?.vendorId && canTag,
  });
  const vendorAccounts = (vendorLedgers?.items ?? []).filter((ledger) => ledger.active);
  // What the invoice is owed on; an advance account has a picker of its own.
  const partyLedgers = vendorAccounts.filter((ledger) => ledger.partyRole !== 'ADVANCE');
  const advanceLedgers = vendorAccounts.filter((ledger) => ledger.partyRole === 'ADVANCE');
  // A pre-filled ledger since deactivated is not offered, nor sent.
  const chosenExpenseLedgerId =
    expenseLedgersLoaded && !expenseLedgers.some((ledger) => ledger.id === expenseLedgerId)
      ? ''
      : expenseLedgerId;
  // Where a pre-filled expense ledger came from, while accounting has not decided yet.
  const expenseLedgerSource =
    !chosenExpenseLedgerId || invoice?.accounting?.expenseLedger
      ? undefined
      : chosenExpenseLedgerId === invoice?.suggestedExpenseLedger?.id
        ? invoice.suggestedExpenseLedger.source === 'VENDOR_DEFAULT'
          ? 'The vendor’s default — confirm or change it.'
          : 'Suggested at review — confirm or change it.'
        : chosenExpenseLedgerId === vendor?.defaultExpenseLedgerId
          ? 'The vendor’s default — confirm or change it.'
          : undefined;

  // Seeded from the vendor master by the extractor, then owned by this form:
  // the deduction proposed at upload, plus the vendor's other profiles as
  // lines ready for the amount each applies to.
  useEffect(() => {
    if (!invoice) return;
    const drafts = draftsOf(invoice);
    setTdsApplicable(invoice.tdsApplicable);
    setLines(
      invoice.tdsApplicable
        ? withVendorProfiles(drafts, vendor?.tdsProfiles ?? [], tdsBaseFor(invoice))
        : drafts,
    );
    // What was tagged before, else the ledger suggested at review, else the
    // vendor's default: accounting confirms it or picks another.
    setExpenseLedgerId(
      invoice.accounting?.expenseLedger?.id ??
        invoice.suggestedExpenseLedger?.id ??
        vendor?.defaultExpenseLedgerId ??
        '',
    );
    setCostCentre(invoice.accounting?.costCentre ?? '');
    setNotes(invoice.accounting?.notes ?? '');
    setRcm(!!invoice.rcmApplicable);
  }, [invoice, vendor]);

  // The vendor's account: the one tagged before, else its default; and its
  // advance account when it has exactly one.
  useEffect(() => {
    if (!invoice) return;
    const accounts = (vendorLedgers?.items ?? []).filter((ledger) => ledger.active);
    const advances = accounts.filter((ledger) => ledger.partyRole === 'ADVANCE');
    setPartyLedgerId(
      invoice.accounting?.partyLedger?.id ?? accounts.find((ledger) => ledger.isDefault)?.id ?? '',
    );
    setAdvanceLedgerId(
      invoice.accounting?.advanceLedger?.id ?? (advances.length === 1 ? advances[0]!.id : ''),
    );
  }, [invoice, vendorLedgers]);

  // Offers as much of the advance as the invoice can take, until the
  // accountant changes it. Re-verifying gives the earlier adjustment back
  // first, so it counts as available here.
  useEffect(() => {
    if (!invoice || !advanceLedger || advanceTouched) return;
    const available = advanceLedger.balance + (invoice.advanceAdjusted ?? 0);
    const offered = Math.min(
      available,
      Math.max(0, (invoice.totalAmount ?? 0) - invoice.tdsAmount),
    );
    setAdvance(offered > 0 ? String(fromMinor(offered)) : '');
  }, [invoice, advanceLedger, advanceTouched]);

  // A profile line left without an amount it applies to is not deducted, and
  // nothing is with TDS calculation switched off in System Settings.
  const applied = company.tds ? parseLines(lines).filter((line) => line.baseAmount > 0) : [];

  const advanceMinor = advance.trim() ? (rupeesToMinor(advance) ?? 0) : 0;

  const verify = useMutation({
    // The advance travels with the click rather than through this closure, so
    // the amount sent is the one on screen even if the click lands between a
    // render and the mutation picking up its new options.
    mutationFn: ({
      action,
      advanceAmount,
    }: {
      action: 'RELEASE' | 'ESCALATE' | 'RETURN';
      advanceAmount: number;
    }) =>
      api.invoices.verify(invoiceId, {
        action,
        advanceAmount: action !== 'RETURN' && advanceAmount > 0 ? advanceAmount : undefined,
        tdsApplicable: tdsApplicable && applied.length > 0,
        tdsLines:
          tdsApplicable && applied.length
            ? applied.map(({ label, section, rateBasisPoints, baseAmount }) => ({
                label,
                section,
                rateBasisPoints,
                baseAmount,
              }))
            : undefined,
        ...(company.india ? { rcmApplicable: rcm } : {}),
        expenseLedgerId: chosenExpenseLedgerId || undefined,
        partyLedgerId: partyLedgerId || undefined,
        advanceLedgerId:
          action !== 'RETURN' && advanceAmount > 0 && advanceLedgerId ? advanceLedgerId : undefined,
        costCentre: costCentre.trim() || undefined,
        notes: notes || undefined,
        priority: action === 'ESCALATE' ? priority : undefined,
        remarks: action === 'RELEASE' ? undefined : remarks,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['accounting-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['vendor-advance'] });
      navigate('/accounting');
    },
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorState error={error} />;
  if (!invoice) return null;

  // Exactly the deductions the request sends, so the preview is what gets stored.
  const preview = previewOf(
    invoice,
    tdsApplicable ? applied : [],
    advanceMinor,
    company.india && rcm ? Math.max(0, invoice.taxAmount ?? 0) : 0,
  );
  // Off in System Settings, nothing is withheld: the server clears any
  // deduction recorded while TDS was on, so none is shown either.
  const showTds = company.tds;
  const advanceAvailable = (advanceLedger?.balance ?? 0) + (invoice.advanceAdjusted ?? 0);
  const showAdvance = !!advanceLedger && advanceAvailable > 0;
  const afterDeductions = Math.max(
    0,
    (invoice.totalAmount ?? 0) -
      preview.tdsAmount -
      (company.india && rcm ? Math.max(0, invoice.taxAmount ?? 0) : 0),
  );
  const advanceProblem =
    advance.trim() && rupeesToMinor(advance) === null
      ? 'Enter an amount, e.g. 25,000'
      : advanceMinor > advanceAvailable
        ? `Only ${money.format(advanceAvailable)} of advance is available`
        : advanceMinor > afterDeductions
          ? `No more than the ${money.format(afterDeductions)} left after TDS and RCM`
          : null;
  const showNetPayable = showTds || showAdvance || (company.india && rcm);
  const figures = 1 + (showTds ? 1 : 0) + (showAdvance ? 1 : 0) + (showNetPayable ? 1 : 0);
  const taxable = tdsBaseFor(invoice);
  // Shown apart from gross, and also deducted from the vendor net payment.
  const rcmAmount = readOnly ? invoice.rcmAmount : Math.max(0, invoice.taxAmount ?? 0);
  const showRcm = company.india && (readOnly ? invoice.rcmApplicable : rcm);
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-5 py-3">
        <div>
          <p className="font-mono text-xs text-slate-500">{invoice.trackingId}</p>
          <h2 className="font-semibold">{invoice.vendorName ?? 'Invoice'}</h2>
          <p className="text-sm text-slate-500">
            {invoice.invoiceNumber ?? '—'} · {formatDate(invoice.invoiceDate)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={invoice.status} />
          <Link className="btn-secondary" to={`/invoices/${invoice.id}`}>
            Open invoice
          </Link>
        </div>
      </div>

      {/* On a phone the figures are rows, label left and amount right. */}
      <dl
        className={`grid gap-2 border-b border-slate-200 px-5 py-4 sm:gap-4 ${
          figures === 4 ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3'
        }`}
      >
        <div className="flex items-baseline justify-between gap-3 sm:block">
          <dt className="text-xs uppercase tracking-wide text-slate-500">
            {showNetPayable ? 'Gross billed' : 'Amount payable'}
          </dt>
          <dd className="text-lg font-semibold">
            <Money minor={invoice.totalAmount ?? 0} />
          </dd>
        </div>
        {showTds ? (
          <div className="flex items-baseline justify-between gap-3 sm:block">
            <dt className="text-xs uppercase tracking-wide text-slate-500">TDS withheld</dt>
            <dd className="text-lg font-semibold text-amber-700">
              {money.format(preview.tdsAmount)}
            </dd>
          </div>
        ) : null}
        {showAdvance ? (
          <div className="flex items-baseline justify-between gap-3 sm:block">
            <dt className="text-xs uppercase tracking-wide text-slate-500">From advance</dt>
            {readOnly ? (
              <dd className="text-lg font-semibold text-brand-700">
                {money.format(preview.advanceAmount)}
              </dd>
            ) : (
              // Edited in place: what is used, out of what the vendor holds,
              // and what stays on the advance afterwards.
              <dd className="text-right sm:text-left">
                <input
                  aria-label="Adjust from vendor advance"
                  title="Comes off what the bank pays; goes back to the vendor if the invoice is returned, rejected or cancelled."
                  className="input tabular w-36 py-1 text-lg font-semibold text-brand-700 sm:w-full"
                  inputMode="decimal"
                  value={advance}
                  onChange={(event) => {
                    setAdvanceTouched(true);
                    setAdvance(event.target.value);
                  }}
                />
                <p className="mt-1 text-xs text-slate-500">
                  <span className="block">
                    {money.format(Math.max(0, advanceAvailable - advanceMinor))} left of{' '}
                    {money.format(advanceAvailable)}
                  </span>
                  <button
                    type="button"
                    className="text-brand-700"
                    onClick={() => {
                      setAdvanceTouched(true);
                      setAdvance(String(fromMinor(Math.min(advanceAvailable, afterDeductions))));
                    }}
                  >
                    Max
                  </button>{' '}
                  ·{' '}
                  <button
                    type="button"
                    className="text-slate-600"
                    onClick={() => {
                      setAdvanceTouched(true);
                      setAdvance('');
                    }}
                  >
                    None
                  </button>
                </p>
              </dd>
            )}
          </div>
        ) : null}
        {showNetPayable ? (
          <div className="flex items-baseline justify-between gap-3 sm:block">
            <dt className="text-xs uppercase tracking-wide text-slate-500">Net payable</dt>
            <dd className="text-lg font-semibold text-emerald-700">
              {money.format(preview.netPayable)}
            </dd>
          </div>
        ) : null}
      </dl>

      {showRcm ? (
        <p className="flex flex-wrap items-baseline justify-between gap-2 border-b border-slate-200 bg-amber-50/60 px-5 py-2 text-sm">
          <span className="text-slate-600">
            GST reversed under RCM, payable by the company and deducted from the vendor payment
          </span>
          <span className="font-semibold text-amber-700">{money.format(rcmAmount)}</span>
        </p>
      ) : null}

      {readOnly ? (
        <div className="px-5 py-4 text-sm text-slate-600">
          This invoice is with Treasury. Its decision is made on the{' '}
          <Link className="text-brand-700" to="/finance-requests">
            Treasury requests
          </Link>{' '}
          screen.
        </div>
      ) : (
        <div className="space-y-4 px-5 py-4">
          {company.tds ? (
            <>
              <label className="block sm:w-48">
                <span className="label">Deduct TDS</span>
                <select
                  className="input"
                  value={tdsApplicable ? 'yes' : 'no'}
                  onChange={(event) => {
                    const yes = event.target.value === 'yes';
                    setTdsApplicable(yes);
                    if (yes) {
                      setLines((current) => withVendorProfiles(current, profiles, taxable));
                    }
                  }}
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </label>

              {tdsApplicable ? (
                <TdsLinesEditor
                  lines={lines}
                  setLines={setLines}
                  profiles={profiles}
                  taxable={taxable}
                  sections={tdsSections}
                  ratesFor={ratesFor}
                  successorOf={successorOf}
                  format={money.format}
                />
              ) : null}

              <p className="text-xs text-slate-500">
                TDS is calculated on the taxable value ({money.format(taxable)}), not on the GST
                charged over it. Each of the vendor's TDS profiles has a line: enter the amount it
                applies to, and lower the first line's amount by as much. A line left without an
                amount is not deducted.
              </p>
            </>
          ) : (
            <p className="text-xs text-slate-500">
              {company.india
                ? 'TDS calculation is switched off for this company in System Settings, so nothing is withheld here: the vendor is paid the full amount.'
                : 'TDS applies to companies registered in India only, so nothing is withheld here: the vendor is paid the full amount.'}
            </p>
          )}

          {company.india ? (
            <RcmCheckbox
              checked={rcm}
              onChange={setRcm}
              gst={invoice.taxAmount ?? 0}
              currency={invoice.currency}
              showAmount={false}
            />
          ) : null}

          {showAdvance && advanceProblem ? (
            <p className="notice-warning text-xs">{advanceProblem}</p>
          ) : showAdvance && advanceMinor > 0 && preview.netPayable === 0 ? (
            <p className="notice-info text-xs">
              Settled in full from the advance: releasing it closes the invoice with no bank
              payment.
            </p>
          ) : null}

          {canTag ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block">
                  <span className="label">Expense ledger</span>
                  <select
                    className="input"
                    value={chosenExpenseLedgerId}
                    onChange={(event) => setExpenseLedgerId(event.target.value)}
                  >
                    <option value="">
                      {expenseLedgers.length
                        ? 'Choose what was bought…'
                        : 'No expense ledgers set up'}
                    </option>
                    <ExpenseLedgerOptions ledgers={expenseLedgers} />
                  </select>
                </label>
                {expenseLedgerSource ? (
                  <span className="mt-1 block text-xs text-ink-500">{expenseLedgerSource}</span>
                ) : null}
                {invoice.accounting?.glCode && !invoice.accounting.expenseLedger ? (
                  <span className="mt-1 block text-xs text-ink-500">
                    GL code typed earlier: {invoice.accounting.glCode}
                  </span>
                ) : null}
              </div>
              <label className="block">
                <span className="label">Vendor account</span>
                <select
                  className="input"
                  value={partyLedgerId}
                  onChange={(event) => setPartyLedgerId(event.target.value)}
                >
                  <option value="">
                    {partyLedgers.length ? 'Choose…' : 'Its Payable account (created on save)'}
                  </option>
                  {partyLedgers.map((ledger) => (
                    <option key={ledger.id} value={ledger.id}>
                      {ledger.code} · {ledger.name}
                      {ledger.isDefault ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
              </label>
              {advanceMinor > 0 && advanceLedgers.length ? (
                <label className="block">
                  <span className="label">Advance account</span>
                  <select
                    className="input"
                    value={advanceLedgerId}
                    onChange={(event) => setAdvanceLedgerId(event.target.value)}
                  >
                    <option value="">Not recorded</option>
                    {advanceLedgers.map((ledger) => (
                      <option key={ledger.id} value={ledger.id}>
                        {ledger.code} · {ledger.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="label">Cost centre</span>
              <input
                className="input"
                value={costCentre}
                maxLength={60}
                onChange={(event) => setCostCentre(event.target.value)}
              />
            </label>
            <label className="block">
              <span className="label">Accounting notes</span>
              <input
                className="input"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
            </label>
          </div>

          <label className="block">
            <span className="label">Remarks</span>
            <textarea
              className="input"
              rows={2}
              value={remarks}
              placeholder="Required when escalating or returning — say why, so the next person does not have to guess."
              onChange={(event) => setRemarks(event.target.value)}
            />
          </label>

          {verify.error ? <ErrorState error={verify.error} /> : null}

          <div className="flex flex-wrap items-center gap-2">
            <button
              className="btn-primary"
              disabled={verify.isPending || !!advanceProblem}
              onClick={() => verify.mutate({ action: 'RELEASE', advanceAmount: advanceMinor })}
            >
              {advanceMinor > 0 && preview.netPayable === 0
                ? 'Verify & settle from advance'
                : 'Verify & release for payment'}
            </button>

            <select
              className="input w-auto"
              value={priority}
              onChange={(event) => setPriority(event.target.value as 'P1' | 'P2')}
            >
              <option value="P1">Priority 1</option>
              <option value="P2">Priority 2</option>
            </select>
            <button
              className="btn-secondary"
              disabled={verify.isPending || !!advanceProblem}
              onClick={() => verify.mutate({ action: 'ESCALATE', advanceAmount: advanceMinor })}
            >
              Escalate to Treasury
            </button>

            <button
              className="btn-secondary"
              disabled={verify.isPending}
              onClick={() => verify.mutate({ action: 'RETURN', advanceAmount: 0 })}
            >
              Return for correction
            </button>
          </div>
        </div>
      )}

      {invoice.accounting?.verifiedAt ? (
        <p className="border-t border-slate-200 px-5 py-3 text-xs text-slate-500">
          Last verified {formatDate(invoice.accounting.verifiedAt)}
          {invoice.accounting.notes ? ` — ${invoice.accounting.notes}` : ''}
        </p>
      ) : null}
    </Card>
  );
}

/**
 * What the deduction would be with the values currently in the form.
 *
 * Uses the same pure function the server does, so the figure shown before
 * submitting is the figure that gets stored.
 */
function previewOf(
  invoice: Invoice,
  parsed: TdsLineInput[],
  advanceAmount: number,
  rcmAmount: number,
) {
  const tdsAmount = totalTds(computeTdsLines(parsed));
  const capped = Math.min(tdsAmount, invoice.totalAmount ?? 0);
  return {
    tdsAmount: capped,
    advanceAmount,
    netPayable: invoiceNetPayableFor(invoice.totalAmount ?? 0, capped, rcmAmount, advanceAmount),
    baseTotal: totalTdsBase(parsed),
  };
}
